import "client-only";

import {
  createTRPCClient,
  createWSClient,
  httpLink,
  httpSubscriptionLink,
  loggerLink,
  splitLink,
  wsLink,
} from "@trpc/client";
import superjson from "superjson";
import type { AppRouter } from "@lattice/backend/trpc";
import { ATOM_frontendEnv_Sync } from "../../jotai/env.atom";
import { getStore } from "../../jotai/store";

const AUTH_REFRESH_RETRY_DELAY_MS = 300;

let pendingAuthRefresh: Promise<boolean> | undefined;

const chatWebSocketClient = createWSClient({
  url: () => {
    if (typeof window === "undefined") {
      return "ws://localhost/api/backend-ws";
    }

    const url = new URL("/api/backend-ws", window.location.href);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    return url.toString();
  },
  lazy: {
    enabled: true,
    closeMs: 5_000,
  },
  keepAlive: {
    enabled: true,
    intervalMs: 15_000,
    pongTimeoutMs: 5_000,
  },
  onOpen: () => {
    console.debug("[chat] WebSocket subscription transport opened");
  },
  onClose: (cause) => {
    console.debug("[chat] WebSocket subscription transport closed", cause);
  },
  onError: (event) => {
    console.debug("[chat] WebSocket subscription transport error", event);
  },
});

function normalizeServerInputUrl(args: {
  input: string | URL | Request;
  baseUrl: string;
}): string {
  const rawUrl =
    args.input instanceof Request ? args.input.url : args.input.toString();

  if (/^https?:\/\//.test(rawUrl)) {
    return rawUrl;
  }

  return new URL(rawUrl, args.baseUrl).toString();
}

function getFirstHeaderValue(value: string | null): string | null {
  if (!value) {
    return null;
  }

  const firstValue = value
    .split(",")
    .map((part) => part.trim())
    .find((part) => part.length > 0);

  return firstValue ?? null;
}

function resolveServerBaseUrl(args: { headerStore: Headers }): string {
  const forwardedHost = getFirstHeaderValue(
    args.headerStore.get("x-forwarded-host"),
  );
  const host = getFirstHeaderValue(args.headerStore.get("host"));
  const rawProtocol = getFirstHeaderValue(
    args.headerStore.get("x-forwarded-proto"),
  );
  const resolvedHost = forwardedHost ?? host;

  if (!resolvedHost) {
    throw new Error("Unable to resolve SSR request host for tRPC transport");
  }

  const protocol =
    rawProtocol === "http" || rawProtocol === "https"
      ? rawProtocol
      : resolvedHost.includes("localhost") ||
          resolvedHost.startsWith("127.0.0.1")
        ? "http"
        : "https";

  return new URL(`${protocol}://${resolvedHost}`).toString();
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function requestAuthRefresh(): Promise<boolean> {
  try {
    const firstResponse = await fetch("/auth/refresh", {
      cache: "no-store",
      credentials: "same-origin",
    });

    if (firstResponse.ok) {
      return true;
    }

    await wait(AUTH_REFRESH_RETRY_DELAY_MS);

    const secondResponse = await fetch("/auth/refresh", {
      cache: "no-store",
      credentials: "same-origin",
    });

    return secondResponse.ok;
  } catch (err) {
    console.debug("Error during auth refresh fetch:", err);
    return false;
  }
}

function refreshAuthSession(): Promise<boolean> {
  if (!pendingAuthRefresh) {
    pendingAuthRefresh = requestAuthRefresh().finally(() => {
      pendingAuthRefresh = undefined;
    });
  }

  return pendingAuthRefresh;
}

function shouldUseWebSocketSubscriptions(path: string): boolean {
  if (path === "chat_v8.streamEvents") {
    return true;
  }

  return (
    getStore().get(ATOM_frontendEnv_Sync)?.CHAT_SUBSCRIPTION_TRANSPORT === "ws"
  );
}

export const trpcClientArgs: Parameters<typeof createTRPCClient<AppRouter>>[0] =
  {
    links: [
      loggerLink({
        enabled: () => {
          return (
            getStore().get(ATOM_frontendEnv_Sync)?.TRPC_LOGGER_ENABLED ?? false
          );
        },
      }),
      splitLink({
        condition: (op) => {
          return (
            op.type === "subscription" &&
            shouldUseWebSocketSubscriptions(op.path)
          );
        },
        true: wsLink<AppRouter>({
          client: chatWebSocketClient,
          transformer: superjson,
        }),
        false: splitLink({
          condition: (op) => {
            return op.type === "subscription";
          },
          true: httpSubscriptionLink({
            url: async () => {
              return "/api/backend";
            },
            transformer: superjson,
          }),
          false: httpLink({
            fetch: async (input, init) => {
              /**
               * TODO: Abstract all of this auth refresh logic into a separate utility function
               */
              if (typeof window === "undefined") {
                const { headers } = await import("next/headers");

                const headerStore = await headers();
                const absoluteInput = normalizeServerInputUrl({
                  input,
                  baseUrl: resolveServerBaseUrl({ headerStore }),
                });
                const forwardedHeaders = new Headers(init?.headers);
                const cookieHeader = headerStore.get("cookie");
                const authHeader = headerStore.get("authorization");

                if (cookieHeader && !forwardedHeaders.has("cookie")) {
                  forwardedHeaders.set("cookie", cookieHeader);
                }

                if (authHeader && !forwardedHeaders.has("authorization")) {
                  forwardedHeaders.set("authorization", authHeader);
                }

                return fetch(absoluteInput, {
                  ...init,
                  body: init?.body,
                  headers: forwardedHeaders,
                });
              }

              // Client-side environment

              const response = await fetch(input, {
                ...init,
                body: init?.body,
              });

              // On 401, try to refresh the access token from the Logto session.
              if (response.status === 401) {
                const refreshSucceeded = await refreshAuthSession();

                // If refresh failed, the user is not authenticated — Prism and
                // the rest of the workspace now require sign-in, so redirect.
                if (!refreshSucceeded) {
                  console.debug("Auth refresh failed - redirecting to sign-in");
                  window.location.href = "/api/auth/sign-in";
                  throw new Error("Unauthorized");
                }

                const retriedResponse = await fetch(input, {
                  ...init,
                  body: init?.body,
                });

                if (retriedResponse.status === 401) {
                  console.debug(
                    "Token was refreshed but still getting 401 - unexpected state",
                  );
                  // Token was refreshed but still getting 401 - unexpected state
                  window.location.href = "/api/auth/sign-in";
                  throw new Error("Unauthorized");
                }
                return retriedResponse;
              }

              return response;
            },
            url: "/api/backend",
            transformer: superjson,
          }),
        }),
      }),
    ],
  };

export const trpcClient: ReturnType<typeof createTRPCClient<AppRouter>> =
  createTRPCClient(trpcClientArgs);
